import { createFileRoute } from '@tanstack/react-router'
import { Home } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { IntegrationCard } from '@/features/integrations/components/IntegrationCard'
import { StripeConnectionDrawer } from '@/features/integrations/components/StripeConnectionDrawer'
import { integrations } from '@/features/integrations/data/integrations'
import type { Integration } from '@/features/integrations/types/integration.types'
import { PageTitle } from '@/shared/components/common/PageTitle'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/shared/components/ui/breadcrumb'
import { Button } from '@/shared/components/ui/button'
import { useBreadcrumbs } from '@/shared/hooks/useBreadcrumbs'

export const Route = createFileRoute('/_auth/developers/integrations')({
  component: IntegrationsPage,
})

function IntegrationsPage() {
  const [connectionStates, setConnectionStates] = useState<
    Record<string, boolean>
  >({})
  const { setBreadcrumbs } = useBreadcrumbs()

  const handleConnectionComplete = (integrationId: string) => {
    setConnectionStates(prev => ({
      ...prev,
      [integrationId]: true,
    }))
  }

  const getCustomDrawer = (integration: Integration) => {
    switch (integration.id) {
      case 'stripe':
        return (
          <StripeConnectionDrawer
            trigger={
              <Button size='sm' className='flex-1'>
                {connectionStates[integration.id] || integration.connected
                  ? 'Manage'
                  : 'Connect'}
              </Button>
            }
            onConnectionComplete={() =>
              handleConnectionComplete(integration.id)
            }
          />
        )
      default:
        return null
    }
  }

  const breadcrumbsContent = useMemo(
    () => (
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink to='/dashboard' className='flex items-center gap-1'>
              <Home className='w-4 h-4' />
              Dashboard
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <span className='text-foreground font-normal'>Developers</span>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>Integrations</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
    ),
    []
  )

  useEffect(() => {
    setBreadcrumbs(breadcrumbsContent)
  }, [setBreadcrumbs, breadcrumbsContent])

  return (
    <div className='container mx-auto py-6'>
      <div className='space-y-6'>
        {/* Header */}
        <div className='flex flex-col gap-4 md:flex-row md:justify-between md:items-start'>
          <div>
            <PageTitle
              title='Integrations'
              subtitle='Connect your favorite tools and services to streamline your workflow'
            />
          </div>
        </div>

        {/* Integration Cards Grid */}
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
          {integrations.map(integration => (
            <IntegrationCard
              key={integration.id}
              integration={integration}
              customDrawer={getCustomDrawer(integration)}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
